import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getAnimals, deleteAnimal, forceDeleteAnimal, getAnimalsByMotherId, addAnimal, updateAnimal, getAnimalsByUser } from "../../../api/animalsApi";
import { getRaces, getRacesE } from "../../../api/raceApi";
import { getPhases, getPhasesE } from "../../../api/phaseApi";
import { getUsers } from "../../../api/usersApi";
import { getStatus, getStatusE } from "../../../api/statusApi";
import { addReproduction, updateReproduction } from "../../../api/reproductionApi";
import '../../eleveur/eleveurAnimalsList/animalsList.css';
import swal from "sweetalert";
import { useTranslation } from "react-i18next";
import { GiCow } from "react-icons/gi";
import { FiSearch, FiX, FiEdit2, FiEye, FiTrash2, FiPlus, FiHash, FiLoader, FiTag, FiCalendar, FiUsers, FiChevronLeft, FiCheckCircle, FiActivity, FiChevronDown } from "react-icons/fi";
import { TbWeight, TbGenderBigender } from "react-icons/tb";
import { MdPets } from "react-icons/md";
import { AuthContext } from "../../../Contexts/AuthContext";

function getApiErrorMessage(error, fallbackMessage) {
  const validationErrors = error?.response?.data?.errors;

  if (validationErrors) {
    return Object.values(validationErrors).flat().join("\n");
  }

  return error?.response?.data?.message || fallbackMessage;
}

function getLatestReproduction(animal) {
  if (!Array.isArray(animal?.reproductions) || animal.reproductions.length === 0) {
    return null;
  }

  return [...animal.reproductions].sort((left, right) => right.id - left.id)[0];
}

function normalizeStatusTitle(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function getAgeInDays(dateString) {
  if (!dateString) {
    return null;
  }

  const birthDate = new Date(dateString);

  if (Number.isNaN(birthDate.getTime())) {
    return null;
  }

  return Math.floor((Date.now() - birthDate.getTime()) / (1000 * 60 * 60 * 24));
}

function findStatusByTitle(statuses, title) {
  if (!title) {
    return null;
  }

  const normalizedTitle = normalizeStatusTitle(title);

  return statuses.find((status) => normalizeStatusTitle(status.titre) === normalizedTitle) || null;
}

function findPhaseByTitle(phases, title) {
  if (!title) {
    return null;
  }

  const normalizedTitle = normalizeStatusTitle(title);

  return phases.find((phase) => normalizeStatusTitle(phase.titre) === normalizedTitle) || null;
}

function sortStatuses(statuses) {
  const statusOrder = {
    croissance: 1,
    oestrus: 2,
    saillie: 3,
    gestation: 4,
    repos: 5,
    velage: 6,
    avortement: 7,
  };

  return [...statuses].sort((left, right) => {
    const leftOrder = statusOrder[normalizeStatusTitle(left?.titre)] ?? Number.MAX_SAFE_INTEGER;
    const rightOrder = statusOrder[normalizeStatusTitle(right?.titre)] ?? Number.MAX_SAFE_INTEGER;

    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }

    return String(left?.titre || '').localeCompare(String(right?.titre || ''));
  });
}

function deriveStatusTitle({ genre, date_naissance, date_saille, date_gestation, date_velage }) {
  if (genre === "male") {
    return "croissance";
  }

  const ageInDays = getAgeInDays(date_naissance);

  if (genre === "femelle" && ageInDays !== null && ageInDays < 365) {
    return "croissance";
  }

  const hasSaillieDate = Boolean(date_saille);
  const hasGestationDate = Boolean(date_gestation);
  const hasVelageDate = Boolean(date_velage);

  if (hasVelageDate && !hasSaillieDate && !hasGestationDate) {
    return "Vêlage";
  }

  if (hasSaillieDate && !hasGestationDate && !hasVelageDate) {
    return "Saillie";
  }

  return "";
}

export default function AdminAnimalList({ userId = null }) {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const isEleveurView = user?.role === 'eleveur';
  const [animals, setAnimals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentAnimalId, setCurrentAnimalId] = useState(null);
  const [currentReproductionId, setCurrentReproductionId] = useState(null);
  const [modalSubmitting, setModalSubmitting] = useState(false);
  const [races, setRaces] = useState([]);
  const [phases, setPhases] = useState([]);
  const [users, setUsers] = useState([]);

  const [form, setForm] = useState({
    numero: "",
    nom: "",
    date_naissance: "",
    poids: "",
    genre: "",
    phase_id: "",
    race_id: "",
    user_id: "",
    mother_id: "",
    date_saille: "",
    date_velage: "",
    date_gestation: "",
    status_id: ""
  });
  const [statuss, setStatuss] = useState([]);

  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedAnimal, setSelectedAnimal] = useState(null);

  const ageInDays = getAgeInDays(form.date_naissance);
  const canUseReproduction = form.genre === 'femelle' && ageInDays !== null && ageInDays >= 365;
  const autoStatusTitle = deriveStatusTitle(form);
  const autoStatus = findStatusByTitle(statuss, autoStatusTitle);
  const croissanceStatus = findStatusByTitle(statuss, 'croissance');
  const gestationStatus = findStatusByTitle(statuss, 'Gestation');
  const reposStatus = findStatusByTitle(statuss, 'Repos');
  const velageStatus = findStatusByTitle(statuss, 'Vêlage');
  const oestrusStatus = findStatusByTitle(statuss, 'Oestrus');
  const hasGestationDates = Boolean(form.date_saille && form.date_gestation);
  const hasOnlyVelageDate = Boolean(form.date_velage && !form.date_saille && !form.date_gestation);
  const isStatusAutoManaged = Boolean(autoStatusTitle) && !hasGestationDates && !hasOnlyVelageDate;
  const veauPhase = findPhaseByTitle(phases, 'Veau');
  const genissePhase = findPhaseByTitle(phases, 'Génisse') || findPhaseByTitle(phases, 'Genisse');
  const vachePhase = findPhaseByTitle(phases, 'Vache');
  const selectedPhase = phases.find((phase) => String(phase.id) === String(form.phase_id));
  const selectedPhaseTitle = normalizeStatusTitle(selectedPhase?.titre);
  const isGenissePhase = selectedPhaseTitle === 'genisse';
  const isVachePhase = selectedPhaseTitle === 'vache';
  const isFemaleInCroissance = form.genre === 'femelle' && ageInDays !== null && ageInDays < 365;
  const selectablePhases = form.genre === 'male'
    ? phases.filter((phase) => phase.id === veauPhase?.id)
    : form.genre === 'femelle'
    ? phases.filter((phase) => {
        if (phase.id === genissePhase?.id) {
          return true;
        }

        if (phase.id === vachePhase?.id) {
          return !isFemaleInCroissance;
        }

        return false;
      })
    : phases;
  const selectableStatuses = sortStatuses(
    hasGestationDates
      ? [gestationStatus, reposStatus].filter(Boolean)
      : hasOnlyVelageDate
      ? [velageStatus, oestrusStatus].filter(Boolean)
      : autoStatus
      ? [autoStatus]
      : statuss.filter((status) => {
        const normalizedTitle = normalizeStatusTitle(status.titre);

        if (normalizedTitle === 'avortement') {
          return false;
        }

        if (isVachePhase && normalizedTitle === 'croissance') {
          return false;
        }

        if (isGenissePhase && normalizedTitle === 'velage') {
          return false;
        }

        return true;
  })
  );

  useEffect(() => {
    setLoading(true);
    const animalsPromise = userId ? getAnimalsByUser(userId) : getAnimals();
    const racesPromise = isEleveurView ? getRacesE() : getRaces();
    const phasesPromise = isEleveurView ? getPhasesE() : getPhases();
    const usersPromise = isEleveurView ? Promise.resolve(user ? [user] : []) : getUsers();
    const statusPromise = isEleveurView ? getStatusE() : getStatus();

    Promise.all([animalsPromise, racesPromise, phasesPromise, usersPromise, statusPromise])
      .then(([animalsData, racesData, phasesData, usersData, statusData]) => {
        setAnimals(animalsData);
        setRaces(racesData);
        setPhases(phasesData);
        setUsers(isEleveurView ? usersData : usersData.filter(u => u.role !== 'admin'));
        setStatuss(statusData);
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [isEleveurView, user, userId]);

  useEffect(() => {
    setForm((previousForm) => {
      const nextForm = { ...previousForm };
      const nextAgeInDays = getAgeInDays(previousForm.date_naissance);
      const shouldClearReproduction = previousForm.genre === 'male'
        || (previousForm.genre === 'femelle' && nextAgeInDays !== null && nextAgeInDays < 365);

      if (shouldClearReproduction) {
        nextForm.date_saille = '';
        nextForm.date_velage = '';
        nextForm.date_gestation = '';
      }

      if (normalizeStatusTitle(selectedPhase?.titre) === 'genisse') {
        nextForm.date_velage = '';
      }

      const nextAutoStatusTitle = deriveStatusTitle(nextForm);
      const nextAutoStatus = findStatusByTitle(statuss, nextAutoStatusTitle);
      const nextAutoStatusId = nextAutoStatus ? String(nextAutoStatus.id) : '';
      const currentStatus = statuss.find((status) => String(status.id) === String(nextForm.status_id));
      const allowedGestationStatusIds = [gestationStatus, reposStatus]
        .filter(Boolean)
        .map((status) => String(status.id));
      const allowedVelageStatusIds = [velageStatus, oestrusStatus]
        .filter(Boolean)
        .map((status) => String(status.id));

      if (normalizeStatusTitle(selectedPhase?.titre) === 'genisse' && normalizeStatusTitle(currentStatus?.titre) === 'velage') {
        nextForm.status_id = '';
      }

      if (nextForm.genre === 'male' && croissanceStatus) {
        nextForm.status_id = String(croissanceStatus.id);
      } else if (nextForm.date_saille && nextForm.date_gestation) {
        if (!allowedGestationStatusIds.includes(String(nextForm.status_id))) {
          nextForm.status_id = gestationStatus ? String(gestationStatus.id) : '';
        }
      } else if (nextForm.date_velage && !nextForm.date_saille && !nextForm.date_gestation) {
        if (!allowedVelageStatusIds.includes(String(nextForm.status_id))) {
          nextForm.status_id = velageStatus ? String(velageStatus.id) : '';
        }
      } else if (nextAutoStatusId && nextForm.status_id !== nextAutoStatusId) {
        nextForm.status_id = nextAutoStatusId;
      }

      const hasChanged = Object.keys(nextForm).some((key) => nextForm[key] !== previousForm[key]);

      return hasChanged ? nextForm : previousForm;
    });
  }, [form.genre, form.phase_id, form.date_naissance, form.date_saille, form.date_velage, form.date_gestation, statuss, selectedPhase, croissanceStatus, gestationStatus, reposStatus, velageStatus, oestrusStatus]);

  useEffect(() => {
    setForm((previousForm) => {
      if (!previousForm.genre) {
        return previousForm;
      }

      if (previousForm.genre === 'male') {
        const veauPhaseId = veauPhase ? String(veauPhase.id) : '';

        if (!veauPhaseId || previousForm.phase_id === veauPhaseId) {
          return previousForm;
        }

        return {
          ...previousForm,
          phase_id: veauPhaseId,
        };
      }

      if (previousForm.genre === 'femelle') {
        const allowedPhaseIds = [genissePhase, !isFemaleInCroissance ? vachePhase : null]
          .filter(Boolean)
          .map((phase) => String(phase.id));

        if (!previousForm.phase_id || allowedPhaseIds.includes(String(previousForm.phase_id))) {
          return previousForm;
        }

        return {
          ...previousForm,
          phase_id: '',
        };
      }

      return previousForm;
    });
  }, [form.genre, veauPhase, genissePhase, vachePhase, isFemaleInCroissance]);

  const filteredAnimals = animals.filter(a => {
    const search = searchTerm.toLowerCase();
    return a.numero?.toString().toLowerCase().includes(search) ||
      a.nom?.toLowerCase().includes(search) ||
      a.race?.nom?.toLowerCase().includes(search) ||
      a.phase?.titre?.toLowerCase().includes(search) ||
      a.poids?.toString().toLowerCase().includes(search) ||
      a.genre?.toLowerCase().includes(search) ||
      a.user?.nom?.toLowerCase().includes(search);
  });

  async function handleDelete(id) {
    const enfants = await getAnimalsByMotherId(id);
    const aEnfants = enfants.length < 1;

    const content = document.createElement('div');
    content.innerHTML = `
      <p style="margin-bottom: 15px;">${t('adminAnimalList.delete.confirmText')}</p>
      ${aEnfants ? `
        <div style="margin-top: 15px; padding: 10px; background-color: #fff3cd; border: 1px solid #ffc107; border-radius: 4px;">
          <label style="display: flex; align-items: center; cursor: pointer; font-weight: bold; color: #856404;">
            <input type="checkbox" id="forceDeleteCheckbox" style="margin-right: 10px; width: 18px; height: 18px; cursor: pointer;" />
            <span>${t('adminAnimalList.delete.forceDelete')}</span>
          </label>
        </div>
      ` : ''}
    `;

    swal({
      title: t('adminAnimalList.delete.confirmTitle'),
      content,
      icon: "warning",
      buttons: [t('adminAnimalList.delete.cancel'), t('adminAnimalList.delete.confirm')],
    }).then(async (good) => {
      if (good) {
        try {
          const force = aEnfants && document.getElementById('forceDeleteCheckbox')?.checked;

          if (force) {
            await forceDeleteAnimal(id);
          } else {
            await deleteAnimal(id);
          }

          setAnimals(an => an.filter(a => a.id !== id));
          swal(t('adminAnimalList.delete.success'), { icon: "success" });
        } catch (e) {
          console.error(e);
          swal(t('adminAnimalList.delete.error'));
        }
      } else {
        swal(t('adminAnimalList.delete.notDeleted'));
      }
    });
  }

  function openCreateModal() {
    setIsEdit(false);
    setCurrentAnimalId(null);
    setCurrentReproductionId(null);
    setForm({
      numero: "",
      nom: "",
      date_naissance: "",
      poids: "",
      genre: "",
      phase_id: "",
      race_id: "",
      user_id: isEleveurView ? String(user?.id || "") : "",
      mother_id: "",
      date_saille: "",
      date_velage: "",
      date_gestation: "",
      status_id: ""
    });
    setIsModalOpen(true);
  }

  function openEditModal(animal) {
    const latestReproduction = getLatestReproduction(animal);

    setIsEdit(true);
    setCurrentAnimalId(animal.id);
    setCurrentReproductionId(latestReproduction?.id || null);
    setForm({
      numero: animal.numero || "",
      nom: animal.nom || "",
      date_naissance: animal.date_naissance || "",
      poids: animal.poids || "",
      genre: animal.genre || "",
      phase_id: animal.phase_id || "",
      race_id: animal.race_id || "",
      user_id: animal.user_id || (isEleveurView ? String(user?.id || "") : ""),
      mother_id: animal.mother_id || "",
      date_saille: latestReproduction?.date_saille || "",
      date_velage: latestReproduction?.date_velage || "",
      date_gestation: latestReproduction?.date_gestation || "",
      status_id: latestReproduction?.status_id || ""
    });
    setIsModalOpen(true);
  }

  function openDetailsModal(animal) {
    setSelectedAnimal(animal);
    setIsDetailsOpen(true);
  }

  function closeModal() {
    if (modalSubmitting) return;
    setIsModalOpen(false);
    setCurrentAnimalId(null);
    setCurrentReproductionId(null);
  }

  function closeDetailsModal() {
    setIsDetailsOpen(false);
    setSelectedAnimal(null);
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((previousForm) => {
      const nextForm = { ...previousForm, [name]: value };

      if (name === 'genre' && value === 'male') {
        nextForm.date_saille = '';
        nextForm.date_velage = '';
        nextForm.date_gestation = '';
      }

      return nextForm;
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setModalSubmitting(true);

    const payload = {
      numero: form.numero,
      nom: form.nom,
      date_naissance: form.date_naissance,
      poids: form.poids,
      genre: form.genre,
      phase_id: form.phase_id,
      race_id: form.race_id,
      user_id: form.user_id,
      mother_id: form.mother_id || null,
      ferme_id: user?.ferme_id || null
    };

    try {
      let animalId = currentAnimalId;
      if (isEdit && currentAnimalId) {
        await updateAnimal(currentAnimalId, payload);
      } else {
        const newAnimal = await addAnimal(payload);
        animalId = newAnimal.id;
      }

      // Add reproduction info if at least one reproduction field is filled or an auto status exists
      if (form.date_saille || form.date_velage || form.date_gestation || form.status_id) {
        const reproductionPayload = {
          animalId,
          date_saille: form.date_saille || null,
          date_velage: form.date_velage || null,
          date_gestation: form.date_gestation || null,
          statusId: form.status_id || null
        };

        if (isEdit && currentReproductionId) {
          await updateReproduction(currentReproductionId, {
            animal_id: animalId,
            date_saille: reproductionPayload.date_saille,
            date_velage: reproductionPayload.date_velage,
            date_gestation: reproductionPayload.date_gestation,
            status_id: reproductionPayload.statusId
          });
        } else {
          const reproduction = await addReproduction(reproductionPayload);

          if (reproduction?.id) {
            setCurrentReproductionId(reproduction.id);
          }
        }
      }

      const refreshed = userId ? await getAnimalsByUser(userId) : await getAnimals();
      setAnimals(refreshed);
      closeModal();
    } catch (err) {
      console.error(err);
      swal(getApiErrorMessage(err, t('adminAddEditAnimal.validation.statusFetchError')), "", "error");
    } finally {
      setModalSubmitting(false);
    }
  }

  return (
    <div className="al-page">
      {/* Header */}
      <div className="al-header">
        <div className="al-header-left">
          <div className="al-header-icon"><GiCow /></div>
          <div>
            <h1 className="al-title">{t('adminAnimalList.title')}</h1>
            <p className="al-subtitle">{t('adminAnimalList.subtitle') || 'Gérez votre troupeau'}</p>
          </div>
        </div>
        <div className="al-header-right">
          <div className="al-stat-pill">
            <FiHash className="al-stat-icon" />
            <span className="al-stat-value">{animals.length}</span>
            <span className="al-stat-label">{t('adminAnimalList.totalLabel') || 'Total'}</span>
          </div>
          <button type="button" className="al-add-btn" onClick={openCreateModal}>
            <FiPlus />
            {t('adminAnimalList.addAnimal')}
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="al-search-bar">
        <FiSearch className="al-search-icon" />
        <input
          type="text"
          className="al-search-input"
          placeholder={t('adminAnimalList.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button className="al-search-clear" onClick={() => setSearchTerm('')}>
            <FiX />
          </button>
        )}
      </div>

      {searchTerm && (
        <div className="al-results-info">
          {t('adminAnimalList.resultsFound', { count: filteredAnimals.length })}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="al-empty-state">
          <FiLoader className="al-empty-icon al-spin" />
          <h3>{t('adminAnimalList.loading')}</h3>
        </div>
      )}

      {/* Empty */}
      {!loading && animals.length === 0 && (
        <div className="al-empty-state">
          <GiCow className="al-empty-icon" />
          <h3>{t('adminAnimalList.noAnimals')}</h3>
          <Link to="/admin/add-animal" className="al-add-btn" style={{ marginTop: '1rem' }}>
            <FiPlus /> {t('adminAnimalList.addAnimal')}
          </Link>
        </div>
      )}

      {/* No search results */}
      {!loading && animals.length > 0 && searchTerm && filteredAnimals.length === 0 && (
        <div className="al-empty-state">
          <FiSearch className="al-empty-icon" />
          <h3>{t('adminAnimalList.noResultsTitle')}</h3>
          <p>{t('adminAnimalList.noResultsText', { term: searchTerm })}</p>
        </div>
      )}

      {/* Table */}
      {!loading && filteredAnimals.length > 0 && (
        <>
          <div className="al-table-wrap">
            <table className="al-table">
              <thead>
                <tr>
                  <th>{t('adminAnimalList.table.animal')}</th>
                  <th>{t('adminAnimalList.table.race')}</th>
                  <th>{t('adminAnimalList.table.phase')}</th>
                  <th>{t('adminAnimalList.table.weight')}</th>
                  <th>{t('adminAnimalList.table.gender')}</th>
                  <th>{t('adminAnimalList.table.responsible')}</th>
                  <th className="al-th-actions">{t('adminAnimalList.table.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredAnimals.map((a, index) => (
                  <tr key={a.id} style={{ '--i': index }}>
                    <td data-label={t('adminAnimalList.table.animal')}>
                      <div className="al-animal-id">
                        <span className="al-numero">#{a.numero}</span>
                        <span className="al-nom">{a.nom}</span>
                      </div>
                    </td>
                    <td data-label={t('adminAnimalList.table.race')}>
                      <span className="al-badge">{a.race?.nom || a.race_id}</span>
                    </td>
                    <td data-label={t('adminAnimalList.table.phase')}>
                      <span className="al-phase">{a.phase?.titre || a.phase_id}</span>
                    </td>
                    <td data-label={t('adminAnimalList.table.weight')}>
                      <span className="al-weight">
                        {a.poids ? <><TbWeight className="al-weight-icon" /> {a.poids} {t('adminAnimalList.table.unitKg') || 'kg'}</> : <span className="al-muted">{t('adminAnimalList.table.notDefined')}</span>}
                      </span>
                    </td>
                    <td data-label={t('adminAnimalList.table.gender')}>
                      <span className={`al-gender ${a.genre === 'femelle' ? 'al-gender-f' : a.genre === 'male' ? 'al-gender-m' : ''}`}>
                        {a.genre === 'femelle'
                          ? t('adminAddEditAnimal.gender.female')
                          : a.genre === 'male'
                          ? t('adminAddEditAnimal.gender.male')
                          : (a.genre || t('adminAnimalList.table.notDefined'))}
                      </span>
                    </td>
                    <td data-label={t('adminAnimalList.table.responsible')}>
                      <div className="al-user">
                        <div className="al-avatar">{a.user?.nom?.charAt(0).toUpperCase() || '?'}</div>
                        <span>{a.user?.nom || t('adminAnimalList.table.notAssigned')}</span>
                      </div>
                    </td>
                    <td data-label={t('adminAnimalList.table.actions')}>
                      <div className="al-actions">
                        <button
                          type="button"
                          className="al-act al-act-edit"
                          title={t('adminAnimalList.actions.edit')}
                          onClick={() => openEditModal(a)}
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          type="button"
                          className="al-act al-act-view"
                          title={t('adminAnimalList.actions.details')}
                          onClick={() => openDetailsModal(a)}
                        >
                          <FiEye />
                        </button>
                        {!isEleveurView && (
                          <button onClick={() => handleDelete(a.id)} className="al-act al-act-del" title={t('adminAnimalList.actions.delete')}>
                            <FiTrash2 />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="al-cards-wrap">
            {filteredAnimals.map((a, index) => (
              <details key={`card-${a.id}`} className="al-card-collapse" style={{ '--i': index }}>
                <summary className="al-card-summary">
                  <div className="al-card-summary-main">
                    <span className="al-numero">#{a.numero}</span>
                    <span className="al-nom">{a.nom}</span>
                  </div>
                  <div className="al-card-summary-side">
                    <span className="al-badge">{a.race?.nom || a.race_id || t('adminAnimalList.table.notDefined')}</span>
                    <FiChevronDown className="al-card-chevron" />
                  </div>
                </summary>

                <div className="al-card-body">
                  <div className="al-card-row">
                    <span className="al-card-label">{t('adminAnimalList.table.phase')}</span>
                    <span className="al-card-value">{a.phase?.titre || a.phase_id || t('adminAnimalList.table.notDefined')}</span>
                  </div>
                  <div className="al-card-row">
                    <span className="al-card-label">{t('adminAnimalList.table.weight')}</span>
                    <span className="al-card-value">
                      {a.poids ? `${a.poids} ${t('adminAnimalList.table.unitKg') || 'kg'}` : t('adminAnimalList.table.notDefined')}
                    </span>
                  </div>
                  <div className="al-card-row">
                    <span className="al-card-label">{t('adminAnimalList.table.gender')}</span>
                    <span className={`al-gender ${a.genre === 'femelle' ? 'al-gender-f' : a.genre === 'male' ? 'al-gender-m' : ''}`}>
                      {a.genre === 'femelle'
                        ? t('adminAddEditAnimal.gender.female')
                        : a.genre === 'male'
                        ? t('adminAddEditAnimal.gender.male')
                        : (a.genre || t('adminAnimalList.table.notDefined'))}
                    </span>
                  </div>
                  <div className="al-card-row">
                    <span className="al-card-label">{t('adminAnimalList.table.responsible')}</span>
                    <span className="al-card-value">{a.user?.nom || t('adminAnimalList.table.notAssigned')}</span>
                  </div>

                  <div className="al-actions al-card-actions">
                    <button
                      type="button"
                      className="al-act al-act-edit"
                      title={t('adminAnimalList.actions.edit')}
                      onClick={() => openEditModal(a)}
                    >
                      <FiEdit2 />
                    </button>
                    <button
                      type="button"
                      className="al-act al-act-view"
                      title={t('adminAnimalList.actions.details')}
                      onClick={() => openDetailsModal(a)}
                    >
                      <FiEye />
                    </button>
                    {!isEleveurView && (
                      <button
                        type="button"
                        onClick={() => handleDelete(a.id)}
                        className="al-act al-act-del"
                        title={t('adminAnimalList.actions.delete')}
                      >
                        <FiTrash2 />
                      </button>
                    )}
                  </div>
                </div>
              </details>
            ))}
          </div>
        </>
      )}

      {isModalOpen && (
        <div className="al-modal-overlay" onClick={closeModal}>
          <div className="al-modal" onClick={(e) => e.stopPropagation()}>
            <div className="al-modal-header">
              <button type="button" className="al-modal-back" onClick={closeModal}>
                <FiChevronLeft />
                {t('adminAddEditAnimal.buttons.back') || 'Back'}
              </button>
              <div className="al-modal-title-wrap">
                <div className="al-modal-icon"><GiCow /></div>
                <div>
                  <h2 className="al-modal-title">
                    {isEdit ? t('adminAddEditAnimal.titleEdit') : t('adminAddEditAnimal.titleAdd')}
                  </h2>
                  <p className="al-modal-subtitle">
                    {isEdit
                      ? (t('adminAddEditAnimal.subtitleEdit') || "Modifier les informations de l'animal")
                      : (t('adminAddEditAnimal.subtitleAdd') || 'Remplissez les informations pour enregistrer un nouvel animal')}
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="al-modal-form">
              <div className="al-modal-section">
                <div className="al-modal-section-header">
                  <div className="al-modal-section-icon"><FiHash /></div>
                  <h3 className="al-modal-section-title">{t('adminAddEditAnimal.sections.identity')}</h3>
                </div>
                <div className="al-modal-grid">
                  <div className="al-modal-field">
                    <label className="al-modal-label">
                      <FiHash className="al-modal-label-icon" />
                      {t('adminAddEditAnimal.fields.numero')}
                    </label>
                    <input
                      type="number"
                      name="numero"
                      className="al-modal-input"
                      value={form.numero}
                      onChange={handleChange}
                      placeholder={t('adminAddEditAnimal.placeholders.numero') || '12345'}
                      min="0"
                      step="1"
                    />
                  </div>
                  <div className="al-modal-field">
                    <label className="al-modal-label">
                      <FiTag className="al-modal-label-icon" />
                      {t('adminAddEditAnimal.fields.nom')}
                    </label>
                    <input
                      type="text"
                      name="nom"
                      className="al-modal-input"
                      value={form.nom}
                      onChange={handleChange}
                      placeholder={t('adminAddEditAnimal.placeholders.nom') || 'EX: Bella'}
                      required
                    />
                  </div>
                  <div className="al-modal-field">
                    <label className="al-modal-label">
                      <FiCalendar className="al-modal-label-icon" />
                      {t('adminAddEditAnimal.fields.dateNaissance')}
                    </label>
                    <input
                      type="date"
                      name="date_naissance"
                      className="al-modal-input"
                      value={form.date_naissance}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="al-modal-field">
                    <label className="al-modal-label">
                      <TbWeight className="al-modal-label-icon" />
                      {t('adminAddEditAnimal.fields.poids')}
                    </label>
                    <input
                      type="number"
                      name="poids"
                      className="al-modal-input"
                      value={form.poids}
                      onChange={handleChange}
                      placeholder={t('adminAddEditAnimal.placeholders.poids') || '450'}
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>

              <div className="al-modal-section">
                <div className="al-modal-section-header">
                  <div className="al-modal-section-icon"><MdPets /></div>
                  <h3 className="al-modal-section-title">{t('adminAddEditAnimal.sections.characteristics')}</h3>
                </div>
                <div className="al-modal-grid">
                  <div className="al-modal-field">
                    <label className="al-modal-label">
                      <TbGenderBigender className="al-modal-label-icon" />
                      {t('adminAddEditAnimal.fields.genre')}
                    </label>
                    <select
                      name="genre"
                      className="al-modal-select"
                      value={form.genre}
                      onChange={handleChange}
                      required
                    >
                      <option value="">{t('adminAddEditAnimal.placeholders.selectGenre')}</option>
                      <option value="male">{t('adminAddEditAnimal.gender.male')}</option>
                      <option value="femelle">{t('adminAddEditAnimal.gender.female')}</option>
                    </select>
                  </div>
                  <div className="al-modal-field">
                    <label className="al-modal-label">
                      <FiActivity className="al-modal-label-icon" />
                      {t('adminAddEditAnimal.fields.phase')}
                    </label>
                    <select
                      name="phase_id"
                      className="al-modal-select"
                      value={form.phase_id}
                      onChange={handleChange}
                      required
                      disabled={form.genre === 'male'}
                    >
                      <option value="">{t('adminAddEditAnimal.placeholders.selectPhase')}</option>
                      {selectablePhases.map(phase => (
                        <option key={phase.id} value={phase.id}>{phase.titre}</option>
                      ))}
                    </select>
                  </div>
                  <div className="al-modal-field">
                    <label className="al-modal-label">
                      <MdPets className="al-modal-label-icon" />
                      {t('adminAddEditAnimal.fields.race')}
                    </label>
                    <select
                      name="race_id"
                      className="al-modal-select"
                      value={form.race_id}
                      onChange={handleChange}
                      required
                    >
                      <option value="">{t('adminAddEditAnimal.placeholders.selectRace')}</option>
                      {races.map(r => (
                        <option key={r.id} value={r.id}>{r.nom}</option>
                      ))}
                    </select>
                  </div>
                  <div className="al-modal-field">
                    <label className="al-modal-label">
                      <MdPets className="al-modal-label-icon" />
                      {t('adminAddEditAnimal.fields.mother')}
                    </label>
                    <select
                      name="mother_id"
                      className="al-modal-select"
                      value={form.mother_id}
                      onChange={handleChange}
                    >
                      <option value="">{t('adminAddEditAnimal.placeholders.selectMother')}</option>
                      {animals.map(an => (
                        <option key={an.id} value={an.id}>{an.numero} - {an.nom}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="al-modal-section">
                <div className="al-modal-section-header">
                  <div className="al-modal-section-icon"><FiUsers /></div>
                  <h3 className="al-modal-section-title">{t('adminAddEditAnimal.sections.assignment')}</h3>
                </div>
                <div className="al-modal-grid al-modal-grid-single">
                  <div className="al-modal-field">
                    <label className="al-modal-label">
                      <FiUsers className="al-modal-label-icon" />
                      {t('adminAddEditAnimal.fields.user')}
                    </label>
                    <select
                      name="user_id"
                      className="al-modal-select"
                      value={form.user_id}
                      onChange={handleChange}
                      required
                    >
                      <option value="">{t('adminAddEditAnimal.placeholders.selectUser')}</option>
                      {users.map(u => (
                        <option key={u.id} value={u.id}>{u.nom}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {(form.genre === 'male' || canUseReproduction || isStatusAutoManaged) && (
                <div className="al-modal-section">
                  <div className="al-modal-section-header">
                    <div className="al-modal-section-icon"><GiCow /></div>
                    <h3 className="al-modal-section-title">{t('adminAddEditAnimal.reproduction.title')}</h3>
                  </div>
                  <div className="al-modal-grid">
                    {canUseReproduction && (
                      <>
                        <div className="al-modal-field">
                          <label className="al-modal-label">
                            <FiCalendar className="al-modal-label-icon" />
                            {t('adminAddEditAnimal.reproduction.dateSaillie')}
                          </label>
                          <input
                            type="date"
                            name="date_saille"
                            className="al-modal-input"
                            value={form.date_saille}
                            onChange={handleChange}
                          />
                        </div>
                        {!isGenissePhase && (
                          <div className="al-modal-field">
                            <label className="al-modal-label">
                              <FiCalendar className="al-modal-label-icon" />
                              {t('adminAddEditAnimal.reproduction.dateVelage')}
                            </label>
                            <input
                              type="date"
                              name="date_velage"
                              className="al-modal-input"
                              value={form.date_velage}
                              onChange={handleChange}
                            />
                          </div>
                        )}
                        <div className="al-modal-field">
                          <label className="al-modal-label">
                            <FiCalendar className="al-modal-label-icon" />
                            {t('adminAddEditAnimal.reproduction.dateGestation')}
                          </label>
                          <input
                            type="date"
                            name="date_gestation"
                            className="al-modal-input"
                            value={form.date_gestation}
                            onChange={handleChange}
                          />
                        </div>
                      </>
                    )}
                    <div className="al-modal-field">
                      <label className="al-modal-label">
                        <FiActivity className="al-modal-label-icon" />
                        {t('adminAddEditAnimal.fields.status')}
                      </label>
                      <select
                        name="status_id"
                        className="al-modal-select"
                        value={form.status_id}
                        onChange={handleChange}
                        disabled={isStatusAutoManaged}
                      >
                        <option value="">{t('adminAddEditAnimal.placeholders.selectStatus')}</option>
                        {selectableStatuses.map((status) => (
                          <option key={status.id} value={status.id}>{status.titre}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              <div className="al-modal-actions">
                <button
                  type="button"
                  className="al-btn-secondary"
                  onClick={closeModal}
                  disabled={modalSubmitting}
                >
                  <FiChevronLeft /> {t('adminAddEditAnimal.buttons.cancel') || 'Cancel'}
                </button>
                <button
                  type="submit"
                  className="al-btn-primary"
                  disabled={modalSubmitting}
                >
                  <FiCheckCircle /> {isEdit ? t('adminAddEditAnimal.buttons.update') : t('adminAddEditAnimal.buttons.add')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isDetailsOpen && selectedAnimal && (
        <div className="al-modal-overlay" onClick={closeDetailsModal}>
          <div className="al-modal al-modal-details" onClick={(e) => e.stopPropagation()}>
            <div className="al-modal-header">
              <button type="button" className="al-modal-back" onClick={closeDetailsModal}>
                <FiChevronLeft />
                {t('adminAddEditAnimal.buttons.back') || 'Back'}
              </button>
              <div className="al-modal-title-wrap">
                <div className="al-modal-icon"><GiCow /></div>
                <div>
                  <h2 className="al-modal-title">{selectedAnimal.nom || t('adminAnimalList.table.animal')}</h2>
                  <p className="al-modal-subtitle">
                    #{selectedAnimal.numero} 
                    {selectedAnimal.race?.nom ? ` • ${selectedAnimal.race.nom}` : ''}
                  </p>
                </div>
              </div>
            </div>

            <div className="al-detail-grid">
              <div className="al-detail-item">
                <span className="al-detail-label">{t('adminAnimalList.table.animal')}</span>
                <span className="al-detail-value">#{selectedAnimal.numero} — {selectedAnimal.nom}</span>
              </div>
              <div className="al-detail-item">
                <span className="al-detail-label">{t('adminAnimalList.table.race')}</span>
                <span className="al-detail-value">{selectedAnimal.race?.nom || selectedAnimal.race_id || t('adminAnimalList.table.notDefined')}</span>
              </div>
              <div className="al-detail-item">
                <span className="al-detail-label">{t('adminAddEditAnimal.fields.dateNaissance')}</span>
                <span className="al-detail-value">{selectedAnimal.date_naissance || t('adminAnimalList.table.notDefined')}</span>
              </div>
              <div className="al-detail-item">
                <span className="al-detail-label">{t('adminAnimalList.table.weight')}</span>
                <span className="al-detail-value">
                  {selectedAnimal.poids
                    ? `${selectedAnimal.poids} ${t('adminAnimalList.table.unitKg') || 'kg'}`
                    : t('adminAnimalList.table.notDefined')}
                </span>
              </div>
              <div className="al-detail-item">
                <span className="al-detail-label">{t('adminAnimalList.table.gender')}</span>
                <span className="al-detail-value">
                  {selectedAnimal.genre === 'femelle'
                    ? t('adminAddEditAnimal.gender.female')
                    : selectedAnimal.genre === 'male'
                    ? t('adminAddEditAnimal.gender.male')
                    : (selectedAnimal.genre || t('adminAnimalList.table.notDefined'))}
                </span>
              </div>
              <div className="al-detail-item">
                <span className="al-detail-label">{t('adminAnimalList.table.phase')}</span>
                <span className="al-detail-value">{selectedAnimal.phase?.titre || selectedAnimal.phase_id || t('adminAnimalList.table.notDefined')}</span>
              </div>
              <div className="al-detail-item">
                <span className="al-detail-label">{t('adminAnimalList.table.responsible')}</span>
                <span className="al-detail-value">{selectedAnimal.user?.nom || t('adminAnimalList.table.notAssigned')}</span>
              </div>
            </div>

            <div className="al-modal-actions al-detail-actions">
              <button
                type="button"
                className="al-btn-secondary"
                onClick={closeDetailsModal}
              >
                <FiChevronLeft /> {t('adminAddEditAnimal.buttons.back') || 'Back'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
